import React from 'react';
import { Copy, Trash2, X } from '../../../bravosIcons';

/**
 * Trainer Library — shared multi-select action bar (Item 3 of
 * `_bmad-output/planning-artifacts/trainer-library-ui-improvements-dev-handoff.md`).
 *
 * Inline dark pill that replaces the per-tab "Actions (N) ▾" dropdown buttons.
 * Renders in the same horizontal band as the tab headers when 1+ rows are
 * selected on the active tab. Slides in (`cubic-bezier(0.32, 0.72, 0, 1)` 0.25s)
 * via the `.lib-selection-bar` CSS keyframe; CSS lives in `styles/library-shell-layout.css`.
 *
 * Per-tab action shape (handoff §4.3 / §5):
 *   Workouts       → Duplicate, Delete
 *   Workout Plans  → Duplicate, Delete
 *   Exercises      → Duplicate (new), Delete
 *
 * No Export, no Assign. Delete uses the warning-red color from `--lib-warn`.
 */
export type LibrarySelectionBarProps = {
  count: number;
  /** Label for the noun shown next to the count — `"workout"`, `"plan"`, `"exercise"`. Pluralised internally. */
  itemNoun: string;
  onDuplicate: () => void;
  onDelete: () => void;
  onClose: () => void;
  /** Disables the Duplicate button (e.g., while a request is in flight). */
  duplicateDisabled?: boolean;
  /** Disables the Delete button. */
  deleteDisabled?: boolean;
};

const LibrarySelectionBar: React.FC<LibrarySelectionBarProps> = ({
  count,
  itemNoun,
  onDuplicate,
  onDelete,
  onClose,
  duplicateDisabled = false,
  deleteDisabled = false,
}) => {
  const plural = count === 1 ? itemNoun : `${itemNoun}s`;
  return (
    <div
      className="lib-selection-bar"
      role="toolbar"
      aria-label={`${count} ${plural} selected — bulk actions`}
    >
      <div className="lib-selection-bar__count">
        <span className="lib-selection-bar__num lib-tnum">{count}</span>
        <span className="lib-selection-bar__label">selected</span>
      </div>

      <span className="lib-selection-bar__divider" aria-hidden="true" />

      <button
        type="button"
        className="lib-selection-bar__action"
        onClick={onDuplicate}
        disabled={duplicateDisabled}
        aria-label={`Duplicate ${count} selected ${plural}`}
      >
        <Copy aria-hidden="true" />
        Duplicate
      </button>

      {/* Second faint rule separates the neutral actions from the destructive
       *  one (mirrors the design HTML — `.action-bar .divider` before Delete). */}
      <span className="lib-selection-bar__divider" aria-hidden="true" />

      <button
        type="button"
        className="lib-selection-bar__action lib-selection-bar__action--danger"
        onClick={onDelete}
        disabled={deleteDisabled}
        aria-label={`Delete ${count} selected ${plural}`}
      >
        <Trash2 aria-hidden="true" />
        Delete
      </button>

      <button
        type="button"
        className="lib-selection-bar__close"
        onClick={onClose}
        aria-label="Clear selection"
      >
        <X aria-hidden="true" />
      </button>
    </div>
  );
};

export default LibrarySelectionBar;
